import { useState } from 'react';
import { Card, Statistic, Table, Tag, Tooltip, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useLanguage } from '@/hooks/useLanguage';
import { formatCurrency } from '../services/costEngine';
import type { CostBreakdown as CostBreakdownType, CostLine } from '../types';

interface Props {
  breakdown: CostBreakdownType;
}

const COLORS = {
  mainAI: '#6366f1',
  internalOps: '#8b5cf6',
  embeddings: '#a78bfa',
  insights: '#f59e0b',
  flows: '#10b981',
  transcription: '#06b6d4',
  infrastructure: '#64748b',
};

const fmt = (v: number) => formatCurrency(v);

interface ReceiptRow {
  key: string;
  category: string;
  item: string;
  cost: number;
  formula: string;
  oneTime?: boolean;  // marks one-time setup costs (vs monthly recurring)
}

function r(key: string, category: string, item: string, line: CostLine): ReceiptRow {
  return { key, category, item, cost: line.cost, formula: line.formula };
}

/**
 * InfoIcon — the (i) glyph rendered next to a title. Pure visual + click
 * handler. The explainer body is rendered separately via <InfoPanel>.
 */
function InfoIcon({ hoverLabel, open, onToggle }: { hoverLabel: string; open: boolean; onToggle: () => void }) {
  const { token } = theme.useToken();
  return (
    <Tooltip
      title={hoverLabel}
      color={token.colorBgContainer}
      overlayInnerStyle={{
        color: token.colorTextSecondary,
        fontSize: 11,
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: '4px 8px',
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <InfoCircleOutlined
        style={{
          cursor: 'pointer',
          opacity: open ? 0.9 : 0.5,
          color: open ? token.colorPrimary : 'inherit',
          fontSize: 12,
          marginInlineStart: 6,
          transition: 'opacity 0.15s, color 0.15s',
        }}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
      />
    </Tooltip>
  );
}

/**
 * InfoPanel — the explainer body. Block-level so the text wraps to the full
 * width of its parent (the card body), never clipped by inline-span context.
 */
/**
 * SegmentChart — a stacked horizontal bar where hovering one segment
 * highlights ONLY that segment (others dim) and shows a tooltip with
 * the segment's name, dollar amount, and a short description.
 */
function SegmentChart({
  data, stackKeys, descriptions, segmentTotal, fmt,
}: {
  data: Record<string, number | string>;
  stackKeys: { key: string; color: string }[];
  descriptions: Record<string, string>;
  segmentTotal: number;
  fmt: (v: number) => string;
}) {
  const { token } = theme.useToken();
  const [hovered, setHovered] = useState<string | null>(null);

  // Custom tooltip — only the hovered segment, with its description
  const ContentTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    // Pick the segment whose dataKey matches the currently-hovered key
    const item = hovered
      ? payload.find((p: any) => p.dataKey === hovered)
      : payload[0];
    if (!item) return null;
    const pct = segmentTotal > 0 ? (item.value / segmentTotal) * 100 : 0;
    return (
      <div style={{
        background: token.colorBgElevated,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: '8px 10px',
        boxShadow: token.boxShadowSecondary,
        maxWidth: 280,
        fontSize: 12,
        color: token.colorText,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
          {item.dataKey}
        </div>
        <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14, marginBlockStart: 4, color: token.colorPrimary }}>
          {fmt(item.value)} <span style={{ fontSize: 11, opacity: 0.65, color: token.colorTextSecondary }}>· {pct.toFixed(1)}%</span>
        </div>
        {descriptions[item.dataKey] && (
          <div style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.5, marginBlockStart: 6 }} dir="auto">
            {descriptions[item.dataKey]}
          </div>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={110}>
      <BarChart data={[data]} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 8 }}>
        <XAxis type="number" tickFormatter={v => fmt(v)} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fontWeight: 600 }} />
        <ChartTooltip
          cursor={false}
          content={<ContentTooltip />}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {stackKeys.map(s => (
          <Bar
            key={s.key}
            dataKey={s.key}
            stackId="a"
            fill={s.color}
            // Dim non-hovered segments; full opacity for hovered (or all if none hovered)
            fillOpacity={hovered == null || hovered === s.key ? 1 : 0.25}
            onMouseEnter={() => setHovered(s.key)}
            onMouseLeave={() => setHovered(null)}
            style={{ cursor: 'pointer', transition: 'fill-opacity 0.15s' }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function InfoPanel({ children }: { children: React.ReactNode }) {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        fontSize: 12,
        color: token.colorTextSecondary,
        fontWeight: 400,
        marginBlockEnd: 10,
        padding: '10px 12px',
        background: token.colorFillAlter,
        borderRadius: token.borderRadius,
        lineHeight: 1.65,
        whiteSpace: 'normal',
        wordBreak: 'normal',
        overflowWrap: 'break-word',
      }}
    >
      {children}
    </div>
  );
}

export function CostBreakdown({ breakdown }: Props) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const [aiInfoOpen, setAiInfoOpen] = useState(false);
  const [infraInfoOpen, setInfraInfoOpen] = useState(false);

  const mainAICostSum = breakdown.agents.reduce(
    (s, a) => s + a.mainAIInput.cost + a.mainAICachedRead.cost + a.mainAICacheWrite.cost + a.mainAIOutput.cost + a.webSearch.cost,
    0,
  );
  const internalOpsSum = breakdown.agents.reduce((s, a) => s + a.title.cost + a.summary.cost, 0);
  const embeddingsSum = breakdown.agents.reduce(
    (s, a) => s + a.ragRetrieval.cost + a.ragRerank.cost
      + a.fileEmbeddingRecurring.cost + a.fileEnrichmentRecurring.cost + a.bundleContextRecurring.cost,
    0,
  );

  // Two-row chart: AI cost stack (one bar) + Infrastructure cost stack (separate bar).
  const aiRow: Record<string, number | string> = { name: t('costSimulator.chart.aiCosts') };
  aiRow[t('costSimulator.breakdown.mainAI')] = mainAICostSum;
  aiRow[t('costSimulator.breakdown.internalOps')] = internalOpsSum;
  aiRow[t('costSimulator.breakdown.embeddings')] = embeddingsSum;
  if (breakdown.insights.cost > 0) aiRow[t('costSimulator.breakdown.insights')] = breakdown.insights.cost;
  if (breakdown.flows.cost > 0) aiRow[t('costSimulator.breakdown.flows')] = breakdown.flows.cost;

  const infraRow: Record<string, number | string> = { name: t('costSimulator.chart.infraCosts') };
  infraRow[t('costSimulator.breakdown.dbStorage')] = breakdown.dbStorage.cost + breakdown.dbCompute.cost;
  infraRow[t('costSimulator.breakdown.gcsStorage')] = breakdown.gcsStorage.cost + breakdown.gcsOps.cost + breakdown.gcsEgress.cost;
  infraRow[t('costSimulator.breakdown.compute')] = breakdown.computeBase.cost + breakdown.computePerUser.cost + breakdown.computeConcurrency.cost;
  infraRow[t('costSimulator.breakdown.redis')] = breakdown.redis.cost;


  const aiStackKeys = [
    { key: t('costSimulator.breakdown.mainAI'), color: COLORS.mainAI },
    { key: t('costSimulator.breakdown.internalOps'), color: COLORS.internalOps },
    { key: t('costSimulator.breakdown.embeddings'), color: COLORS.embeddings },
    breakdown.insights.cost > 0 && { key: t('costSimulator.breakdown.insights'), color: COLORS.insights },
    breakdown.flows.cost > 0 && { key: t('costSimulator.breakdown.flows'), color: COLORS.flows },
  ].filter(Boolean) as { key: string; color: string }[];

  const infraStackKeys = [
    { key: t('costSimulator.breakdown.dbStorage'), color: '#475569' },
    { key: t('costSimulator.breakdown.gcsStorage'), color: '#64748b' },
    { key: t('costSimulator.breakdown.compute'), color: '#94a3b8' },
    { key: t('costSimulator.breakdown.redis'), color: '#cbd5e1' },
  ];


  // Cache savings — what the SAME cached tokens would have cost at the full
  // input price. We divide the cached-read cost by its multiplier (typically
  // 0.10 for Anthropic/OpenAI, 0.25 for Google/Moonshot) to recover the
  // "without caching" cost, then subtract.
  // Approximation: assume Anthropic-style 0.10× multiplier (~10× back).
  // This understates savings for providers with smaller discounts.
  const cacheReadPaid = breakdown.agents.reduce((s, a) => s + a.mainAICachedRead.cost, 0);
  const cacheWritePaid = breakdown.agents.reduce((s, a) => s + a.mainAICacheWrite.cost, 0);
  // Full-price equivalent of the cached tokens = paid × (1 / multiplier)
  const cacheReadWouldHaveCost = cacheReadPaid * 10;
  const cacheSavings = Math.max(0, cacheReadWouldHaveCost - cacheReadPaid - cacheWritePaid);

  // Build receipt rows
  const rows: ReceiptRow[] = [];
  for (const a of breakdown.agents) {
    if (a.mainAIInput.cost > 0)       rows.push(r(`${a.agentId}-in`, a.agentName, t('costSimulator.breakdown.inputUncached'), a.mainAIInput));
    if (a.mainAICachedRead.cost > 0)  rows.push(r(`${a.agentId}-cached`, a.agentName, t('costSimulator.breakdown.inputCached'), a.mainAICachedRead));
    if (a.mainAICacheWrite.cost > 0)  rows.push(r(`${a.agentId}-cw`, a.agentName, t('costSimulator.breakdown.cacheWrite'), a.mainAICacheWrite));
    if (a.mainAIOutput.cost > 0)      rows.push(r(`${a.agentId}-out`, a.agentName, t('costSimulator.breakdown.output'), a.mainAIOutput));
    if (a.webSearch.cost > 0)         rows.push(r(`${a.agentId}-ws`, a.agentName, t('costSimulator.breakdown.webSearch'), a.webSearch));
    if (a.ragRetrieval.cost > 0)      rows.push(r(`${a.agentId}-rag`, a.agentName, t('costSimulator.breakdown.ragQuery'), a.ragRetrieval));
    if (a.ragRerank.cost > 0)         rows.push(r(`${a.agentId}-rerank`, a.agentName, t('costSimulator.breakdown.ragRerank'), a.ragRerank));
    if (a.fileEmbeddingSetup.cost > 0)      rows.push({ ...r(`${a.agentId}-emb-s`, a.agentName, t('costSimulator.breakdown.fileEmbedding'), a.fileEmbeddingSetup), oneTime: true });
    if (a.fileEmbeddingRecurring.cost > 0)  rows.push(r(`${a.agentId}-emb-r`, a.agentName, t('costSimulator.breakdown.fileEmbedding'), a.fileEmbeddingRecurring));
    if (a.fileEnrichmentSetup.cost > 0)     rows.push({ ...r(`${a.agentId}-enr-s`, a.agentName, t('costSimulator.breakdown.fileEnrichment'), a.fileEnrichmentSetup), oneTime: true });
    if (a.fileEnrichmentRecurring.cost > 0) rows.push(r(`${a.agentId}-enr-r`, a.agentName, t('costSimulator.breakdown.fileEnrichment'), a.fileEnrichmentRecurring));
    if (a.bundleContextSetup.cost > 0)      rows.push({ ...r(`${a.agentId}-bc-s`, a.agentName, t('costSimulator.breakdown.bundleContext'), a.bundleContextSetup), oneTime: true });
    if (a.bundleContextRecurring.cost > 0)  rows.push(r(`${a.agentId}-bc-r`, a.agentName, t('costSimulator.breakdown.bundleContext'), a.bundleContextRecurring));
    if (a.title.cost > 0)             rows.push(r(`${a.agentId}-title`, a.agentName, t('costSimulator.breakdown.titleGeneration'), a.title));
    if (a.summary.cost > 0)           rows.push(r(`${a.agentId}-summary`, a.agentName, t('costSimulator.breakdown.summaryGeneration'), a.summary));
  }
  if (breakdown.insights.cost > 0)        rows.push(r('insights', t('costSimulator.breakdown.platform'), t('costSimulator.breakdown.insights'), breakdown.insights));
  if (breakdown.flows.cost > 0)           rows.push(r('flows', t('costSimulator.breakdown.platform'), t('costSimulator.breakdown.flows'), breakdown.flows));
  rows.push(r('db-storage', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.dbStorage'), breakdown.dbStorage));
  rows.push(r('db-compute', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.dbCompute'), breakdown.dbCompute));
  if (breakdown.gcsStorage.cost > 0)      rows.push(r('gcs-storage', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.gcsStorage'), breakdown.gcsStorage));
  if (breakdown.gcsOps.cost > 0)          rows.push(r('gcs-ops', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.gcsOps'), breakdown.gcsOps));
  if (breakdown.gcsEgress.cost > 0)       rows.push(r('gcs-egress', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.gcsEgress'), breakdown.gcsEgress));
  rows.push(r('compute-base', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.computeBase'), breakdown.computeBase));
  rows.push(r('compute-user', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.computePerUser'), breakdown.computePerUser));
  rows.push(r('compute-conc', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.computeConcurrency'), breakdown.computeConcurrency));
  rows.push(r('redis', t('costSimulator.breakdown.infrastructure'), t('costSimulator.breakdown.redis'), breakdown.redis));

  // Column widths use percentages so the table fills its container exactly
  // and never forces horizontal scroll.
  const columns = [
    { title: t('costSimulator.table.category'), dataIndex: 'category', key: 'category', width: '30%', ellipsis: true },
    {
      title: t('costSimulator.table.lineItem'),
      dataIndex: 'item',
      key: 'item',
      ellipsis: true,
      render: (item: string, r: ReceiptRow) => (
        <span>
          {item}
          {r.oneTime && (
            <Tag
              style={{
                marginInlineStart: 6, fontSize: 9, lineHeight: '14px',
                padding: '0 5px', border: 'none',
                background: '#fef3c7', color: '#92400e', fontWeight: 600,
              }}
            >
              {t('costSimulator.oneTime')}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t('costSimulator.table.monthlyCost'),
      dataIndex: 'cost',
      key: 'cost',
      align: 'right' as const,
      width: 90,
      render: (v: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(v)}</span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Card size="small">
          <Statistic
            title={t('costSimulator.kpi.total')}
            value={breakdown.grandTotal}
            precision={2}
            prefix="$"
            valueStyle={{ color: token.colorPrimary, fontSize: 22 }}
          />
          {breakdown.pricingVersion && (
            <div style={{ fontSize: 10, opacity: 0.5 }}>
              pricing v{breakdown.pricingVersion} · models v{breakdown.modelsVersion}
            </div>
          )}
        </Card>
        <Card size="small">
          <Statistic
            title={t('costSimulator.kpi.aiOnly')}
            value={breakdown.totalAICost}
            precision={2}
            prefix="$"
            valueStyle={{ color: COLORS.mainAI, fontSize: 22 }}
          />
        </Card>
        <Card size="small">
          <Statistic
            title={t('costSimulator.kpi.infra')}
            value={breakdown.totalInfrastructureCost}
            precision={2}
            prefix="$"
            valueStyle={{ color: COLORS.infrastructure, fontSize: 22 }}
          />
        </Card>
        <Card size="small">
          <Statistic
            title={t('costSimulator.kpi.peakConcurrent')}
            value={breakdown.peakConcurrentUsers}
            valueStyle={{ fontSize: 22 }}
          />
        </Card>
        {breakdown.totalSetupCost > 0 && (
          <Card size="small" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
            <Statistic
              title={
                <Tooltip title={t('costSimulator.kpi.setupTooltip')}>
                  <span style={{ color: '#92400e' }}>
                    {t('costSimulator.kpi.setup')} <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.6 }} />
                  </span>
                </Tooltip>
              }
              value={breakdown.totalSetupCost}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#b45309', fontSize: 22 }}
            />
            <div style={{ fontSize: 10, color: '#92400e', opacity: 0.75 }}>
              {t('costSimulator.kpi.setupNote', { firstMonth: breakdown.firstMonthTotal.toFixed(2) })}
            </div>
          </Card>
        )}
        {cacheSavings > 0 && (
          <Card size="small" style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
            <Statistic
              title={
                <Tooltip title={t('costSimulator.kpi.savedTooltip')}>
                  <span style={{ color: '#166534' }}>
                    {t('costSimulator.kpi.saved')} <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.6 }} />
                  </span>
                </Tooltip>
              }
              value={cacheSavings}
              precision={2}
              prefix="-$"
              valueStyle={{ color: '#16a34a', fontSize: 22 }}
            />
            <div style={{ fontSize: 10, color: '#166534', opacity: 0.7 }}>
              {t('costSimulator.kpi.savedNote')}
            </div>
          </Card>
        )}
      </div>

      {/* Two SEPARATE charts — each on its own scale, explainer hidden behind (i) */}
      <Card size="small" title={
        <span>
          {t('costSimulator.chart.aiTitle')}
          <InfoIcon hoverLabel={t('costSimulator.chart.explainHint')} open={aiInfoOpen} onToggle={() => setAiInfoOpen(o => !o)} />
        </span>
      }>
        {aiInfoOpen && <InfoPanel>{t('costSimulator.chart.aiExplainer')}</InfoPanel>}
        <SegmentChart
          data={aiRow}
          stackKeys={aiStackKeys}
          descriptions={{
            [t('costSimulator.breakdown.mainAI')]: t('costSimulator.chart.segment.mainAI'),
            [t('costSimulator.breakdown.internalOps')]: t('costSimulator.chart.segment.internalOps'),
            [t('costSimulator.breakdown.embeddings')]: t('costSimulator.chart.segment.embeddings'),
            [t('costSimulator.breakdown.insights')]: t('costSimulator.chart.segment.insights'),
            [t('costSimulator.breakdown.flows')]: t('costSimulator.chart.segment.flows'),
          }}
          segmentTotal={breakdown.totalAICost}
          fmt={fmt}
        />
        <div style={{ fontSize: 11, opacity: 0.55, marginBlockStart: 6, paddingInlineStart: 4 }}>
          <strong>{t('costSimulator.chart.aiTotal')}:</strong> {fmt(breakdown.totalAICost)}
        </div>
      </Card>

      <Card size="small" title={
        <span>
          {t('costSimulator.chart.infraTitle')}
          <InfoIcon hoverLabel={t('costSimulator.chart.explainHint')} open={infraInfoOpen} onToggle={() => setInfraInfoOpen(o => !o)} />
        </span>
      }>
        {infraInfoOpen && <InfoPanel>{t('costSimulator.chart.infraExplainer')}</InfoPanel>}
        <SegmentChart
          data={infraRow}
          stackKeys={infraStackKeys}
          descriptions={{
            [t('costSimulator.breakdown.dbStorage')]: t('costSimulator.chart.segment.dbStorage'),
            [t('costSimulator.breakdown.gcsStorage')]: t('costSimulator.chart.segment.gcsStorage'),
            [t('costSimulator.breakdown.compute')]: t('costSimulator.chart.segment.compute'),
            [t('costSimulator.breakdown.redis')]: t('costSimulator.chart.segment.redis'),
          }}
          segmentTotal={breakdown.totalInfrastructureCost}
          fmt={fmt}
        />
        <div style={{ fontSize: 11, opacity: 0.55, marginBlockStart: 6, paddingInlineStart: 4 }}>
          <strong>{t('costSimulator.chart.infraTotal')}:</strong> {fmt(breakdown.totalInfrastructureCost)}
        </div>
      </Card>

      {/* Per-agent stats */}
      {breakdown.agents.length > 0 && (
        <Card size="small" title={t('costSimulator.agentStats.title')}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {breakdown.agents.map(a => (
              <div key={a.agentId} style={{ padding: 10, background: token.colorFillAlter, borderRadius: token.borderRadius }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBlockEnd: 6 }}>{a.agentName}</div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>
                  {t('costSimulator.agentStats.totalCost')}: <strong>{fmt(a.totalCost)}</strong>
                </div>
                <div style={{ fontSize: 11, opacity: 0.75 }} dir="auto">
                  {t('costSimulator.agentStats.perConv')}: {fmt(a.costPerConversation)}
                </div>
                <div style={{ fontSize: 11, opacity: 0.75 }} dir="auto">
                  {t('costSimulator.agentStats.perUser')}: {fmt(a.costPerActiveUser)}
                </div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>
                  {t('costSimulator.agentStats.cacheHit')}: {(a.effectiveCacheHitPct * 100).toFixed(0)}%
                </div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>
                  {t('costSimulator.agentStats.avgInputPerTurn')}: {Math.round(a.avgInputTokensPerTurn).toLocaleString()} tok
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Receipt table with expandable formula rows */}
      <Card size="small" title={t('costSimulator.table.title')}>
        <div style={{ fontSize: 11, opacity: 0.6, marginBlockEnd: 8 }}>
          {t('costSimulator.table.hint')}
        </div>
        <Table
          dataSource={[...rows].sort((a, b) => Number(!!b.oneTime) - Number(!!a.oneTime))}
          columns={columns}
          size="small"
          pagination={false}
          tableLayout="fixed"
          scroll={undefined}
          expandable={{
            expandedRowRender: (record: ReceiptRow) => (
              <div style={{
                fontSize: 12,
                fontFamily: 'monospace',
                padding: 8,
                background: token.colorFillAlter,
                borderRadius: token.borderRadius,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                <div style={{ opacity: 0.6, marginBlockEnd: 4 }}>{t('costSimulator.table.formula')}:</div>
                <div>{record.formula}</div>
                <div style={{ marginBlockStart: 6, opacity: 0.6 }}>
                  = <strong style={{ color: token.colorPrimary }}>{fmt(record.cost)}</strong> / month
                </div>
              </div>
            ),
            rowExpandable: (record) => Boolean(record.formula),
          }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <strong>{t('costSimulator.table.grandTotal')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong style={{ color: token.colorPrimary }}>{fmt(breakdown.grandTotal)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>
    </div>
  );
}
